import { useMemo, useRef } from 'react'
import { parse, isValid, isAfter } from 'date-fns'
import { useSelector } from 'react-redux'
import * as orderHelpers from '@/local/server-data/domain/helpers/order'
import { selectTerritory } from '@/features/domain/territory/cursors'
import { selectUserConfiguration } from '@/features/domain/user'
import { selectRoutes } from '@/features/domain/route/cursors'
import { selectVehicles, selectVehiclesByVehicleId } from '@/features/domain/vehicle/cursors'

export function useExecutionOrderActions(extOrder: uui.domain.client.rm.ExtendedOrderStep) {
  const userConfiguration = useSelector(selectUserConfiguration)
  const routes = useSelector(selectRoutes)
  const nowRef = useRef(new Date())
  const order = extOrder

  const isOrderAssignedToFutureRoute = useMemo(() => {
    const routeHolderOfOrder = Object.values(routes).find(r => {
      const isInThisRoute = r.route.steps.find(step => step.ref === order.order.id)
      return isInThisRoute
    })
    if (!routeHolderOfOrder) {
      return false
    }
    const routeDate = parse(routeHolderOfOrder.id.slice(-8), 'yyyyMMdd', nowRef.current)
    const todayDate = parse(userConfiguration.today, 'yyyyMMdd', nowRef.current)

    if (isValid(routeDate)) {
      return isAfter(routeDate, todayDate)
    }

    return false
  }, [routes, userConfiguration, order])

  const { rescheduleBehavior } = useSelector(selectTerritory)
  const vehiclesByVehicleId = useSelector(selectVehiclesByVehicleId)
  const vehicles = useSelector(selectVehicles)

  const canExportExecutionOrder =
    orderHelpers.isOrderStepExecuted(extOrder.orderStep, rescheduleBehavior) ||
    extOrder.status === 'notCompleted'

  const routeApproved = !extOrder.isUnassigned && extOrder.isRouteApproved

  const vehicle = extOrder.hasVehicle
    ? vehicles[vehiclesByVehicleId[extOrder.vehicleId]]
    : undefined

  const vehicleTracked = !!vehicle ? vehicle.hasRoutingLicense && vehicle.vehicle.tracked : false

  const canEditExecutionEvents =
    extOrder.hasVehicle && routeApproved && !isOrderAssignedToFutureRoute && vehicleTracked

  return {
    canEditExecutionEvents,
    canExportExecutionOrder,
    isOrderAssignedToFutureRoute,
  }
}
